/**
 * Why an audit has no insight, told in the audit's own terms.
 *
 * The read API collapses every non-completed audit into one `NOT_APPLICABLE` status, so the
 * reason has to be recovered here from the audit's live status. Two of them are waiting for
 * something and three of them are not, and a reader owed "this will never happen" should
 * not be told "this has not happened yet".
 */
import { AuditStatus } from '@amzn/gbsa-helios-typescript-client';
import { MessageBannerType } from '@amzn/stencil-react-components/message-banner';

import type { AuditInsightsMessageKey } from '../translations';

/** The banner one incomplete audit gets. */
export interface IncompleteReason {
  titleKey: AuditInsightsMessageKey;
  bodyKey: AuditInsightsMessageKey;
  type: MessageBannerType;
}

/**
 * An audit still on its way to being completed. Informational: the sections are coming.
 *
 * `SCHEDULED` and `NOT_STARTED` share this body — both mean no one has begun, and drawing a
 * distinction the reader cannot act on would only add a string to translate.
 */
const AWAITING: IncompleteReason = {
  titleKey: 'incompleteOpenTitle',
  bodyKey: 'incompleteNotStartedText',
  type: MessageBannerType.Informational,
};

const UNDER_WAY: IncompleteReason = {
  titleKey: 'incompleteOpenTitle',
  bodyKey: 'incompleteInProgressText',
  type: MessageBannerType.Informational,
};

/**
 * An audit that closed without completing. Warning rather than informational: this is the
 * one case where a reader who came for insights leaves without them permanently, and the
 * wording alone asks to be read before that lands.
 */
const EXPIRED: IncompleteReason = {
  titleKey: 'incompleteExpiredTitle',
  bodyKey: 'incompleteClosedText',
  type: MessageBannerType.Warning,
};

const CANCELLED: IncompleteReason = {
  titleKey: 'incompleteCancelledTitle',
  bodyKey: 'incompleteClosedText',
  type: MessageBannerType.Warning,
};

/**
 * A status this build has not learned, or an audit that has not loaded yet.
 *
 * Says only what holds for every non-completed audit, so a status Helios adds later reads
 * as vague rather than wrong.
 */
const UNKNOWN: IncompleteReason = {
  titleKey: 'incompleteOpenTitle',
  bodyKey: 'incompleteUnknownText',
  type: MessageBannerType.Informational,
};

/**
 * A completed audit the read API still calls not-applicable.
 *
 * The two read the record at different moments, so a just-completed or reopened audit can
 * be reported completed here and have no insight record yet. Without an entry this fell to
 * `UNKNOWN`, which tells a completed audit its insights arrive "once this audit is
 * completed".
 *
 * The heading is the pending one, since that is the state. The body is not: `pendingText`
 * says the page keeps re-checking, and `pollWhilePending` re-reads only while the read
 * itself reports `PENDING` — this banner is reached on `NOT_APPLICABLE`, where polling is
 * already stopped, so that wording would promise a refresh that never comes.
 */
const PREPARING: IncompleteReason = {
  titleKey: 'pendingTitle',
  bodyKey: 'incompletePreparingText',
  type: MessageBannerType.Informational,
};

// A Map rather than an indexed object: the lookup key is a variable, which an object index
// would make a lint finding.
const REASONS = new Map<string, IncompleteReason>([
  [AuditStatus.COMPLETED, PREPARING],
  [AuditStatus.SCHEDULED, AWAITING],
  [AuditStatus.NOT_STARTED, AWAITING],
  [AuditStatus.IN_PROGRESS, UNDER_WAY],
  [AuditStatus.EXPIRED, EXPIRED],
  [AuditStatus.CANCELLED, CANCELLED],
]);

/**
 * The banner for an audit that has no insight.
 *
 * @param status - The audit's live status. Absent while the summary is still loading.
 */
export function incompleteReason(status: string | undefined): IncompleteReason {
  return (status && REASONS.get(status)) || UNKNOWN;
}
